import React, {
    useMemo,
} from "react";
import {
    Col,
    Form,
    OverlayTrigger,
    Row,
    Tooltip,
} from "react-bootstrap";
import {
    useFormikContext,
} from "formik";
import SmartSearchSelect from "../../../components/Smart/SmartSearchSelect";
import {
    numOrNull,
} from "../adapters";

const FormSection = ({
    title,
    children,
    className = "mb-4",
}) => (
    <section className={`form-section ${className}`}>
        <h5 className="text-muted fw-medium mb-3">
            {title}
        </h5>

        {children}
    </section>
);

const HelpTooltip = ({
    id,
    children,
}) => (
    <OverlayTrigger
        placement="top"
        trigger={[
            "hover",
            "focus",
            "click",
        ]}
        overlay={
            <Tooltip
                id={`${id}-tooltip`}
            >
                {children}
            </Tooltip>
        }
    >
        <button
            type="button"
            className="
                btn
                btn-link
                d-inline-flex
                align-items-center
                justify-content-center
                p-0
                ms-1
                text-muted
                border-0
                shadow-none
            "
            aria-label="More information"
            style={{
                width: 28,
                height: 28,
                lineHeight: 1,
                textDecoration: "none",
            }}
        >
            <span
                className="
                    d-inline-flex
                    align-items-center
                    justify-content-center
                    rounded-circle
                    border
                "
                style={{
                    width: 17,
                    height: 17,
                    fontSize: 11,
                    fontWeight: 600,
                }}
            >
                ?
            </span>
        </button>
    </OverlayTrigger>
);

export default function DetailTab({
    meta,
    isEdit = false,
}) {
    const {
        setFieldValue,
        values,
    } = useFormikContext();

    const modelsOptions = useMemo(
        () =>
            (meta.models || []).map(
                (model) => ({
                    value: model.id,

                    label:
                        `${model.Brand?.name || ""} ${model.name || ""}`.trim(),

                    licenseTypes:
                        Array.isArray(
                            model.licenseTypes
                        )
                            ? model.licenseTypes
                            : [],
                })
            ),
        [meta.models]
    );

    const shopsOptions = useMemo(
        () =>
            (meta.shops || []).map(
                (shop) => ({
                    value: shop.id,

                    label:
                        `${shop?.Location?.name ?? ""} - ${shop.name}`,
                })
            ),
        [meta.shops]
    );

    const selectedLicenseTypes =
        String(
            values.rentalPolicy
                ?.licenseType ?? ""
        )
            .split(",")
            .map((item) =>
                item
                    .trim()
                    .toUpperCase()
            )
            .filter(Boolean);

    return (
        <>
            <FormSection title="Motorcycle & Shops">
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Model{" "}
                                <span className="text-danger">
                                    *
                                </span>
                            </Form.Label>

                            {isEdit ? (
                                <Form.Control
                                    value={
                                        modelsOptions.find(
                                            (
                                                option
                                            ) =>
                                                String(
                                                    option.value
                                                ) ===
                                                String(
                                                    values.motorcycleModelId
                                                )
                                        )
                                            ?.label ||
                                        `${values.MotorcycleModel?.Brand?.name || ""} ${values.MotorcycleModel?.name || ""}`.trim()
                                    }
                                    disabled
                                    readOnly
                                />
                            ) : (
                                <SmartSearchSelect
                                    currentID={
                                        values.motorcycleModelId
                                    }
                                    options={
                                        modelsOptions
                                    }
                                    onChange={(
                                        option
                                    ) => {
                                        setFieldValue(
                                            "motorcycleModelId",
                                            option?.value ||
                                            ""
                                        );

                                        const licenseTypes =
                                            Array.isArray(
                                                option?.licenseTypes
                                            )
                                                ? option.licenseTypes
                                                    .map(
                                                        (
                                                            item
                                                        ) =>
                                                            String(
                                                                item
                                                            )
                                                                .trim()
                                                                .toUpperCase()
                                                    )
                                                    .filter(
                                                        Boolean
                                                    )
                                                : [];

                                        setFieldValue(
                                            "rentalPolicy.licenseType",
                                            [
                                                ...new Set(
                                                    licenseTypes
                                                ),
                                            ].join(
                                                ","
                                            )
                                        );
                                    }}
                                    placeholder="Select model..."
                                />
                            )}
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Shops{" "}
                                <span className="text-danger">
                                    *
                                </span>
                            </Form.Label>

                            <SmartSearchSelect
                                currentID={
                                    values.shopIds
                                }
                                options={
                                    shopsOptions
                                }
                                onChange={(
                                    selectedOptions
                                ) => {
                                    setFieldValue(
                                        "shopIds",
                                        (
                                            selectedOptions ||
                                            []
                                        ).map(
                                            (
                                                option
                                            ) =>
                                                option.value
                                        )
                                    );
                                }}
                                placeholder="Select shops..."
                                isMulti
                                closeMenuOnSelect={
                                    false
                                }
                                maxVisibleMultiTags={
                                    3
                                }
                                allowExpandMultiTags
                            />

                            <Form.Text className="text-muted">
                                {isEdit
                                    ? "Selected shops will be activated and fully synchronized. Removing a shop will make this motorcycle unavailable there."
                                    : "The motorcycle will be created or reactivated and synchronized in every selected shop."}
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>
            </FormSection>

            <FormSection title="Rider Requirements">
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Minimum Rider Age
                            </Form.Label>

                            <Form.Control
                                type="number"
                                min={0}
                                value={
                                    values
                                        .rentalPolicy
                                        ?.minAge ??
                                    ""
                                }
                                onChange={(event) =>
                                    setFieldValue(
                                        "rentalPolicy.minAge",
                                        numOrNull(
                                            event
                                                .target
                                                .value
                                        )
                                    )
                                }
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Required Riding Experience
                            </Form.Label>

                            <Form.Control
                                type="number"
                                min={0}
                                value={
                                    values
                                        .rentalPolicy
                                        ?.minExp ??
                                    ""
                                }
                                onChange={(event) =>
                                    setFieldValue(
                                        "rentalPolicy.minExp",
                                        numOrNull(
                                            event
                                                .target
                                                .value
                                        )
                                    )
                                }
                            />

                            <Form.Text className="text-muted">
                                Years
                            </Form.Text>
                        </Form.Group>
                    </Col>

                    <Col xs={12}>
                        <Form.Group className="mb-3">
                            <Form.Label className="mb-2">
                                License Classes
                            </Form.Label>

                            <Row className="g-3 align-items-center">
                                {[
                                    "AM",
                                    "B",
                                    "A1",
                                    "A2",
                                    "A",
                                ].map(
                                    (
                                        licenseType
                                    ) => {
                                        const isChecked =
                                            selectedLicenseTypes.includes(
                                                licenseType
                                            );

                                        return (
                                            <Col
                                                xs="auto"
                                                key={
                                                    licenseType
                                                }
                                                className="pe-2"
                                            >
                                                <Form.Check
                                                    type="switch"
                                                    id={`rentalPolicy.licenseType.${licenseType}`}
                                                    label={
                                                        licenseType
                                                    }
                                                    checked={
                                                        isChecked
                                                    }
                                                    onChange={(
                                                        event
                                                    ) => {
                                                        const next =
                                                            event
                                                                .target
                                                                .checked
                                                                ? [
                                                                    ...new Set(
                                                                        [
                                                                            ...selectedLicenseTypes,
                                                                            licenseType,
                                                                        ]
                                                                    ),
                                                                ]
                                                                : selectedLicenseTypes.filter(
                                                                    (
                                                                        item
                                                                    ) =>
                                                                        item !==
                                                                        licenseType
                                                                );

                                                        setFieldValue(
                                                            "rentalPolicy.licenseType",
                                                            next.join(
                                                                ","
                                                            )
                                                        );
                                                    }}
                                                />
                                            </Col>
                                        );
                                    }
                                )}
                            </Row>
                        </Form.Group>
                    </Col>
                </Row>
            </FormSection>

            <FormSection title="Promotion" className="mb-0">
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <div
                                className="
                                    d-flex
                                    justify-content-between
                                    align-items-center
                                    gap-3
                                "
                            >
                                <div className="d-flex align-items-center">
                                    <Form.Label
                                        htmlFor="bestOffer"
                                        className="mb-0"
                                    >
                                        Best Offer
                                    </Form.Label>

                                    <HelpTooltip id="best-offer">
                                        Mark this motorcycle as a highlighted offer in customer-facing results.
                                    </HelpTooltip>
                                </div>

                                <Form.Check
                                    type="switch"
                                    id="bestOffer"
                                    label={
                                        values.bestOffer
                                            ? "Enabled"
                                            : "Disabled"
                                    }
                                    checked={Boolean(
                                        values.bestOffer
                                    )}
                                    onChange={(event) =>
                                        setFieldValue(
                                            "bestOffer",
                                            event.target.checked
                                        )
                                    }
                                    className="mb-0 flex-shrink-0"
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
            </FormSection>
        </>
    );
}